'use client'

import { supabase } from '@/supabase'
import { Session } from '@supabase/supabase-js'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'

export type SessionState =
  | {
      type: 'LOGGED_IN'
      data: Session
    }
  | {
      type: 'LOGGED_OUT'
    }
  | {
      type: 'LOADING'
    }

type Context = {
  state: SessionState
  update: () => Promise<void>
}

const SessionContext = createContext<Context>({
  state: { type: 'LOADING' },
  update: async () => {},
})

export const SessionProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [data, setData] = useState<SessionState>({
    type: 'LOADING',
  })
  const setup = async () => {
    try {
      const result = await supabase.auth.refreshSession()
      if (result.error) {
        console.error('get session failed', result.error)
        setData({ type: 'LOGGED_OUT' })
      } else {
        if (result.data.session === null) {
          setData({
            type: 'LOGGED_OUT',
          })
        } else {
          setData({
            type: 'LOGGED_IN',
            data: result.data.session,
          })
        }
      }
    } catch (error) {
      console.error('get session error', error)
      setData({ type: 'LOGGED_OUT' })
    }
  }

  useEffect(() => {
    setup()
    const interval = setInterval(setup, 1000 * 60 * 10)
    return () => {
      clearInterval(interval)
    }
  }, [])

  const session = useMemo(() => {
    return { state: data, update: setup }
  }, [data])

  return (
    <SessionContext.Provider value={session}>
      {children}
    </SessionContext.Provider>
  )
}

export const useSession = () => {
  const chatState = useContext(SessionContext)
  if (chatState === null)
    throw 'Session not found. This function possibly run outside of context provider.'
  return chatState
}
